import { IDashboardWidget } from '../../../types/IDashboardWidget';
import { FunctionComponent, useEffect, useState } from 'react';
import { AverageCycleCountWidgetConfiguration } from './AverageCycleCountWidgetConfiguration';

import AverageCycleCountWidgetConfigurationForm from './AverageCycleCountWidgetConfigurationForm';
import DashboardWidgetConfigurationButton from '../../dashboard-widget/DashboardWidgetConfigurationButton';
import DashboardWidgetContent from '../../dashboard-widget/DashboardWidgetContent';
import { DashboardWidgetProvider } from '../../dashboard-widget/DashboardWidgetContextProvider';
import DashboardWidgetConfigurationDrawer from '../../dashboard-widget/DasshboardWidgetConfigurationDrawer';
import { WidgetCountAndTitle } from '../base/WidgetCountAndTitle';
import { assetService } from '@/modules/assets/api/assets/asset.service';
import { useTimerEndListener } from '@/providers/timer-provider/useTimerEndListener';
import { useTranslation } from '@/lib';

type AverageCycleCountWidgetProps = IDashboardWidget<AverageCycleCountWidgetConfiguration>;

const AverageCycleCountWidget: FunctionComponent<AverageCycleCountWidgetProps> = ({ configuration, id }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [averageCycles, setAverageCycles] = useState(0);

  // Listens to the timer context
  useTimerEndListener(handleTimerEnd, setIsLoading);

  // The required filters need to be set before we can fetch data.
  // The cycle starting point is either a location or one or more label values; one of them is required.
  const isConfigured =
    configuration.assetTypeId !== undefined &&
    (configuration.cycleStartLocationId !== undefined || (configuration.cycleStartLabelValueIds?.length ?? 0) > 0);

  async function fetchAverageCycleCount() {
    if (!isConfigured) {
      setIsLoading(false);
      return;
    }

    const response = await assetService.getAverageCycleCount({
      assetTypeId: configuration.assetTypeId!,
      cycleStartLocationId: configuration.cycleStartLocationId,
      cycleStartLabelValueIds: configuration.cycleStartLabelValueIds,
      currentLocationId: configuration.currentLocationId,
      currentLabelValueIds: configuration.currentLabelValueIds,
      startDateUTC: configuration.startDateUTC,
      endDateUTC: configuration.endDateUTC,
    });

    if (response.isSuccess) {
      setAverageCycles(response.payload.averageCycles);
      setIsError(false);
    } else {
      setIsError(true);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetchAverageCycleCount();
  }, [
    configuration.assetTypeId,
    configuration.cycleStartLocationId,
    configuration.cycleStartLabelValueIds,
    configuration.currentLocationId,
    configuration.currentLabelValueIds,
    configuration.startDateUTC,
    configuration.endDateUTC,
  ]);

  function handleTimerEnd() {
    fetchAverageCycleCount();
  }

  return (
    <DashboardWidgetProvider
      widgetId={id ?? 'unknown'}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      isError={isError}
      setIsError={setIsError}
    >
      <DashboardWidgetContent>
        <DashboardWidgetConfigurationButton></DashboardWidgetConfigurationButton>

        {isConfigured ? (
          <WidgetCountAndTitle count={averageCycles} title={configuration.title} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center px-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('dashboard.averageCycleCountWidget.notConfigured')}
          </div>
        )}
      </DashboardWidgetContent>

      <DashboardWidgetConfigurationDrawer widgetName={configuration.title}>
        <AverageCycleCountWidgetConfigurationForm configuration={configuration} id={id} />
      </DashboardWidgetConfigurationDrawer>
    </DashboardWidgetProvider>
  );
};

export default AverageCycleCountWidget;
